import { MprcMessageBox } from 'components';
import { TrainingAccessTimeResult } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EnrollmentViewPreChecks } from './EnrollmentViewPreChecks';

/////////////////////////////////////////////////////////////////////////////////////

export interface EnrollmentAccess {
    isBlocked: boolean,
    blockContent?: React.ReactNode;
}

/////////////////////////////////////////////////////////////////////////////////////

export function useEnrollmentCheckAccessTime(enrollmentId: string, isBlocked: boolean, setIsBlocked: (value: EnrollmentAccess) => void, onReload: () => void) {

    const api = useCoreApi();

    const [accessTimeRestriction, setAccessTimeRestriction] = useState<TrainingAccessTimeResult | null>();

    const timeout = 60000; //1 minuto
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /////////////////////////////////////////////////////////////////////////////////////

    ///
    const handleAccessNotAllowed = (result: TrainingAccessTimeResult) => {
        setIsBlocked({ isBlocked: true, blockContent: <MprcMessageBox message={result.message ?? ""} variant="warning" /> });
    };

    ///
    const disableTimer = useCallback(() => {
        if (timerRef.current != null) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, [timerRef]);

    ///
    const enableTimer = useCallback(() => {
        if (timerRef.current == null) {
            timerRef.current = setInterval(() => {
                const dt = new Date();
                if (accessTimeRestriction!.canAccess && accessTimeRestriction!.endOfWorkDay != null && accessTimeRestriction!.endOfWorkDay < dt) {
                    handleAccessNotAllowed(accessTimeRestriction!);
                    disableTimer();
                }

                if (!accessTimeRestriction!.canAccess && accessTimeRestriction!.nextStartWorkDay != null && accessTimeRestriction!.nextStartWorkDay < dt) {
                    setIsBlocked({ isBlocked: false });
                    disableTimer();
                    onReload();
                }
            }, timeout);
        }
    }, [accessTimeRestriction, timerRef]);

    useEffect(() => {
        return () => {
            disableTimer();
        };
    }, []);

    useEffect(() => {
        if (accessTimeRestriction != null) {
            timerRef.current = null;
            enableTimer();
        }
        else {
            disableTimer();
        }
    }, [accessTimeRestriction]);

    function checkEnrollmentAccess() {
        return new Promise((resolve, reject) => {
            api.v1.enrollment.getEnrollmentViewPreChecks({ enrollmentId, checkAccessTimeRules: accessTimeRestriction === undefined })
                .then((response) => {
                    const canAccess = response.data.blockReason == null && response.data.trainingAgreementPending == false;
                    setIsBlocked({
                        isBlocked: !canAccess,
                        blockContent: !canAccess && <EnrollmentViewPreChecks enrollmentId={enrollmentId} preChecks={response.data} onReload={onReload} />
                    });

                    if (response.data.trainingAccessTimeRestriction != null) {
                        setAccessTimeRestriction(response.data.trainingAccessTimeRestriction);
                    }

                    resolve(canAccess);
                })
                .catch(e => reject(e));
        });
    }

    return { checkEnrollmentAccess };
}
